<script setup lang="ts">
const route = useRoute()
const { setBreadcrumb } = useLsBreadcrumb()
const NuxtLinkComponent = resolveComponent('NuxtLink')
interface FeedbackItem {
  id?: string | number
  name: string
  avatar?: string
  submittedAt?: string
  country?: string
  time?: string
  content: string
}

interface SiteVisitInfo {
  id?: string | number
  detailKey?: string
  isexhibition?: boolean
  title?: string
  summary?: string
  content?: string
  riskLabel?: string
  riskBackgroundColor?: string
  riskFontColor?: string
  riskBorderColor?: string
  flag?: string
  country?: string
  location?: string
  date?: string
  images?: string[]
  mapImages?: string[]
  sortTime?: number
}

interface SurveyLabel {
  text?: string
  borderColor?: string
  backgroundColor?: string
  fontColor?: string
}

interface SurveyItem {
  surveyId?: string
  urlPath?: string
  isexhibition?: boolean
  surveyDate?: string
  surveyTimestamp?: number
  images?: string[] | null
  flag?: string
  country?: string
  city?: string
  address?: string
  title?: string
  summary?: string
  labels?: SurveyLabel[] | null
  detail?: {
    content?: string
    baiduImg?: string
    googleImg?: string
  } | null
}

interface SurveyInfo {
  total?: number
  items?: SurveyItem[] | null
}

const COMBO_VISIT_PAGE_SIZE = 2
const SITE_VISIT_PAGE_SIZE = 1
const SITE_VISIT_MAX_ITEMS = 5
const CAROUSEL_INTERVAL = 3000

const { t, localePath } = useAppI18n()
const { isRtl } = useRtl()

const normalizeApiText = (value: unknown) => (value === null || value === undefined ? '' : String(value).trim())
const stripHtmlTags = (value: string) =>
  value
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/\s+/g, ' ')
    .trim()
const normalizeSummaryText = (value: unknown) => stripHtmlTags(normalizeApiText(value))
const getSurveySortTime = (item: SurveyItem) => {
  const numericTime = Number(item.surveyTimestamp)

  if (Number.isFinite(numericTime) && numericTime > 0) {
    return numericTime < 1e12 ? numericTime * 1000 : numericTime
  }

  return 0
}

const uniqueTextList = (values: Array<string | null | undefined>) => {
  const seen = new Set<string>()
  const list: string[] = []

  values.forEach((value) => {
    const text = normalizeApiText(value)

    if (!text || seen.has(text)) {
      return
    }

    seen.add(text)
    list.push(text)
  })

  return list
}

const getSurveyPrimaryLabel = (item: SurveyItem) =>
  item.labels?.find((label) => Boolean(normalizeApiText(label.text))) || null

const normalizeSurveyItem = (item: SurveyItem): SiteVisitInfo | null => {
  const detail = item.detail || {}
  const title = normalizeApiText(item.title)
  const images = uniqueTextList(item.images || [])
  const mapImages = uniqueTextList([detail.baiduImg, detail.googleImg])

  if (!title || images.length === 0) {
    return null
  }

  const surveyLabel = getSurveyPrimaryLabel(item)
  const detailKey = normalizeApiText(item.urlPath)

  return {
    id: normalizeApiText(item.surveyId),
    detailKey,
    isexhibition: Boolean(item.isexhibition),
    title,
    summary: normalizeSummaryText(item.summary),
    content: normalizeSummaryText(detail.content),
    riskLabel: normalizeApiText(surveyLabel?.text),
    riskBackgroundColor: normalizeApiText(surveyLabel?.backgroundColor),
    riskFontColor: normalizeApiText(surveyLabel?.fontColor),
    riskBorderColor: normalizeApiText(surveyLabel?.borderColor),
    flag: normalizeApiText(item.flag),
    country: normalizeApiText(item.country),
    location: uniqueTextList([item.country, normalizeApiText(item.city), normalizeApiText(item.address)]).join(' · '),
    date: normalizeApiText(item.surveyDate),
    images,
    mapImages,
    sortTime: getSurveySortTime(item),
  }
}

const props = withDefaults(
  defineProps<{
    survey?: SurveyInfo | null
    feedbackItems?: FeedbackItem[]
    feedbackCount?: number
    showFeedbackCount?: boolean
    showFeedback?: boolean
    showVisitMap?: boolean
    exchangeName?: string
    exchangeId?: string
  }>(),
  {
    survey: null,
    feedbackItems: () => [],
    feedbackCount: 0,
    showFeedbackCount: true,
    showFeedback: false,
    showVisitMap: false,
    exchangeName: '',
    exchangeId: '',
  },
)

const currentVisitPage = ref(0)
let visitTimer: ReturnType<typeof setInterval> | null = null
const normalizedSiteVisits = computed(() => {
  const visits =
    props.survey?.items
      ?.map((item) => normalizeSurveyItem(item))
      .filter((item): item is SiteVisitInfo => Boolean(item)) || []

  return visits.toSorted((a, b) => (b.sortTime || 0) - (a.sortTime || 0)).slice(0, SITE_VISIT_MAX_ITEMS)
})
const siteVisitCount = computed(() => normalizedSiteVisits.value.length)
const siteVisitPages = computed(() => {
  const pages: SiteVisitInfo[][] = []

  for (let index = 0; index < normalizedSiteVisits.value.length; index += SITE_VISIT_PAGE_SIZE) {
    pages.push(normalizedSiteVisits.value.slice(index, index + SITE_VISIT_PAGE_SIZE))
  }

  return pages
})

// 实勘项数据
const currentSiteVisitPageItems = computed(() => {
  const pages = siteVisitPages.value
  const pageIndex = Math.min(Math.max(currentVisitPage.value, 0), pages.length - 1)
  return pages[pageIndex] || []
})

const siteVisitPageCount = computed(() => siteVisitPages.value.length)
const shouldShowVisitSwitch = computed(() => siteVisitPageCount.value > 1)
// RTL 下左右按钮沿阅读方向切换，LTR 继续保持原有页码顺序。
const previousVisitPageStep = computed(() => (isRtl.value ? 1 : -1))
const nextVisitPageStep = computed(() => (isRtl.value ? -1 : 1))
const previousVisitButtonLabel = computed(() => t(isRtl.value ? '82414' : '82919'))
const nextVisitButtonLabel = computed(() => t(isRtl.value ? '82919' : '82414'))
const getVisitPageButtonLabel = (index: number) => `${t('82447')} ${t('82521')} ${index + 1}`
const allVisitFlags = computed(() => uniqueTextList(normalizedSiteVisits.value.map((item) => item.flag)))
const hasSiteVisit = computed(() =>
  currentSiteVisitPageItems.value.some((item) => Boolean(item.title) && (item.images?.length || 0) > 0),
)
const shouldShowSiteVisit = computed(() => hasSiteVisit.value)
// 无实勘时至少需要 3 条有效反馈，避免满宽区域只展示零散卡片；有实勘时保持原有展示逻辑。
const validFeedbackItemCount = computed(
  () => props.feedbackItems.filter((item) => item.name.trim() && item.content.trim()).length,
)
const shouldShowFeedback = computed(
  () => props.showFeedback && (shouldShowSiteVisit.value || validFeedbackItemCount.value >= 3),
)
const shouldShowVisitStandalone = computed(() => shouldShowSiteVisit.value && !shouldShowFeedback.value)
const shouldShowVisitMap = computed(() => props.showVisitMap || shouldShowVisitStandalone.value)
const shouldRenderPanel = computed(() => shouldShowSiteVisit.value || shouldShowFeedback.value)

const getVisitDetailPath = (visit: SiteVisitInfo) => {
  if (!visit.detailKey) {
    return undefined
  }

  return localePath({
    name: visit?.isexhibition ? 'inspections-expo-exchange-slug' : 'inspections-exchange-slug',
    params: {
      slug: visit.detailKey,
    },
  })
}
const getVisitPhotos = (visit: SiteVisitInfo) => (visit.images || []).slice(0, COMBO_VISIT_PAGE_SIZE)
const getVisitMediaItems = (visit: SiteVisitInfo) => {
  const photos = getVisitPhotos(visit).map((src) => ({ type: 'photo' as const, src }))
  const map = shouldShowVisitMap.value ? visit.mapImages?.[0] : ''

  return map ? [...photos, { type: 'map' as const, src: map }] : photos
}
const getVisitMetaText = (visit: SiteVisitInfo) =>
  [normalizeApiText(visit.country), visit.date].filter(Boolean).join(' · ')
const getVisitBadgeStyle = (visit: SiteVisitInfo) => {
  const backgroundColor = normalizeApiText(visit.riskBackgroundColor)
  const color = normalizeApiText(visit.riskFontColor)
  const borderColor = normalizeApiText(visit.riskBorderColor)

  return {
    backgroundColor: backgroundColor || undefined,
    color: color || undefined,
    borderColor: borderColor || undefined,
    borderStyle: borderColor ? 'solid' : undefined,
    borderWidth: borderColor ? '1px' : undefined,
  }
}

const clampVisitPage = () => {
  const pageCount = siteVisitPageCount.value

  if (pageCount === 0) {
    currentVisitPage.value = 0
    return
  }

  currentVisitPage.value = Math.min(currentVisitPage.value, pageCount - 1)
}

const setVisitPage = (nextPage: number) => {
  const pageCount = siteVisitPageCount.value

  if (pageCount <= 0) {
    currentVisitPage.value = 0
    return
  }

  currentVisitPage.value = (nextPage + pageCount) % pageCount
}

const stopVisitAutoplay = () => {
  if (!visitTimer) {
    return
  }

  clearInterval(visitTimer)
  visitTimer = null
}

const startVisitAutoplay = () => {
  stopVisitAutoplay()

  const canAutoPlay = siteVisitPageCount.value > 1

  if (!canAutoPlay) {
    return
  }

  visitTimer = setInterval(() => {
    setVisitPage(currentVisitPage.value + 1)
  }, CAROUSEL_INTERVAL)
}

watch(normalizedSiteVisits, () => {
  clampVisitPage()
  startVisitAutoplay()
})

onMounted(() => {
  startVisitAutoplay()
})

onBeforeUnmount(() => {
  stopVisitAutoplay()
})

const matomo = useMatomo()
const trackSurveyClick = (type: '滑动' | '详情' | '更多') => {
  matomo.trackEvent('broker_survey_click', {
    af_type: type,
    af_name: props.exchangeId || '',
    af_code: props.exchangeName || '',
  })
}

const handleVisitPageClick = (nextPage: number) => {
  trackSurveyClick('滑动')
  setVisitPage(nextPage)
}

// 设置实勘的面包屑
const setInspectionsBreadcrumb = (visit: SiteVisitInfo) => {
  trackSurveyClick('详情')
  if (!visit.detailKey) {
    return
  }
  setBreadcrumb(visit.detailKey, {
    label: props.exchangeName,
    route: route.fullPath,
  })
}
</script>

<template>
  <section
    v-if="shouldRenderPanel"
    class="site-feedback-panel"
    :class="{
      'site-feedback-panel-with-feedback': shouldShowFeedback,
      'site-feedback-panel-cooperative': shouldShowVisitMap && shouldShowSiteVisit,
      'site-feedback-panel-site-visit-only': shouldShowVisitStandalone,
    }"
  >
    <div v-if="shouldShowSiteVisit" class="site-visit-region">
      <div class="section-heading">
        <h2>{{ t('82521') }} {{ siteVisitCount }}</h2>
        <div v-if="allVisitFlags.length > 0" class="country-group" aria-hidden="true">
          <span v-for="flag in allVisitFlags" :key="flag" class="country-flag">
            <CommonImage :src="flag" alt="" class="size-full" loading="eager" />
          </span>
        </div>
      </div>

      <div class="visit-carousel" @mouseenter="stopVisitAutoplay" @mouseleave="startVisitAutoplay">
        <button
          v-if="shouldShowVisitSwitch"
          type="button"
          class="visit-arrow visit-arrow-prev"
          :aria-label="previousVisitButtonLabel"
          @click="handleVisitPageClick(currentVisitPage + previousVisitPageStep)"
        >
          <span class="visit-arrow-glyph" dir="ltr">‹</span>
        </button>

        <div
          v-for="(visitPage, pageIndex) in siteVisitPages"
          v-show="pageIndex === currentVisitPage"
          :key="pageIndex"
          class="site-visit-page"
        >
          <component
            v-for="visit in visitPage"
            :key="visit.id || visit.title"
            :is="visit.detailKey ? NuxtLinkComponent : 'div'"
            class="site-visit-item"
            :class="{ 'site-visit-item-link': visit.detailKey }"
            :to="visit.detailKey ? getVisitDetailPath(visit) : undefined"
            :target="visit.detailKey ? '_blank' : undefined"
            :rel="visit.detailKey ? 'noopener noreferrer' : undefined"
            @click="visit.detailKey && setInspectionsBreadcrumb(visit)"
          >
            <div class="visit-copy">
              <h3>{{ visit.title }}</h3>
              <p v-if="shouldShowVisitStandalone && (visit.summary || visit.content)" class="visit-summary">
                {{ visit.summary || visit.content }}
              </p>
              <div class="visit-meta">
                <span v-if="visit.riskLabel" class="visit-status-badge" :style="getVisitBadgeStyle(visit)">
                  {{ visit.riskLabel }}
                </span>
                <p v-if="getVisitMetaText(visit)">{{ getVisitMetaText(visit) }}</p>
              </div>
            </div>

            <div class="visit-gallery" :class="{ 'visit-gallery-cooperative': shouldShowVisitMap }">
              <div
                v-for="(media, index) in getVisitMediaItems(visit)"
                :key="`${visit.id || visit.title}-${media.type}-${media.src}-${index}`"
                class="visit-image-wrap"
                :class="{ 'visit-image-wrap-map': media.type === 'map' }"
              >
                <CommonImage :src="media.src" alt="WikiBit site visit" class="visit-image size-full" loading="eager" />
                <span v-if="media.type === 'map'" class="visit-map-pin" aria-hidden="true">
                  <span class="visit-map-pin-ring"></span>
                  <span class="visit-map-pin-shadow"></span>
                  <span class="visit-map-pin-body"></span>
                </span>
              </div>
            </div>
          </component>
        </div>

        <button
          v-if="shouldShowVisitSwitch"
          type="button"
          class="visit-arrow visit-arrow-next"
          :aria-label="nextVisitButtonLabel"
          @click="handleVisitPageClick(currentVisitPage + nextVisitPageStep)"
        >
          <span class="visit-arrow-glyph" dir="ltr">›</span>
        </button>
      </div>

      <div v-if="shouldShowVisitSwitch" class="visit-dots" :aria-label="t('82521')">
        <button
          v-for="(_, index) in siteVisitPages"
          :key="index"
          type="button"
          class="visit-dot"
          :class="{ 'visit-dot-active': index === currentVisitPage }"
          :aria-label="getVisitPageButtonLabel(index)"
          :aria-current="index === currentVisitPage"
          @click="handleVisitPageClick(index)"
        ></button>
      </div>
    </div>

    <div v-if="shouldShowSiteVisit && shouldShowFeedback" class="site-feedback-divider" aria-hidden="true"></div>

    <div
      v-if="shouldShowFeedback"
      class="feedback-region"
      :class="{ 'feedback-region-full': !shouldShowSiteVisit }"
      data-exchange-feedback-preview
    >
      <ExchangeDetailUserFeedbackCard
        :has-site-visit="shouldShowSiteVisit"
        :feedback-items="feedbackItems"
        :feedback-count="feedbackCount"
        :show-feedback-count="showFeedbackCount"
        :exchange-name="exchangeName"
        :exchange-id="exchangeId"
      />
    </div>
  </section>
</template>

<style scoped lang="less">
.site-feedback-panel {
  --panel-text: #292a2e;
  --panel-text-subtle: #7d818a;
  --panel-text-subtlest: #8c8f97;
  --panel-divider: rgba(5, 21, 36, 0.08);
  --panel-hover: rgba(5, 21, 36, 0.04);
  --write-feedback-bottom-safe-area: 120px;

  position: relative;
  display: flex;
  width: 100%;
  padding-bottom: 36px;
  align-items: stretch;
  gap: 0;
  color: var(--panel-text);
}

.site-visit-region {
  position: relative;
  display: flex;
  width: 100%;
  max-width: none;
  flex: 1 1 auto;
  flex-direction: column;
}

.site-feedback-panel-with-feedback .site-visit-region {
  max-width: 628px;
  padding-inline-end: 32px;
}

.section-heading {
  display: flex;
  width: 100%;
  align-items: center;
  padding-bottom: 20px;
}

.section-heading h2,
.feedback-heading h2 {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  color: var(--panel-text);
  font-size: 18px;
  font-weight: 600;
  line-height: 26px;
}

.country-group {
  display: flex;
  flex-shrink: 0;
  align-items: center;
}

.country-flag {
  display: inline-flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px solid rgba(5, 21, 36, 0.06);
  border-radius: 999px;
  background: rgba(255, 255, 255, 1);

  & + & {
    margin-inline-start: -4px;
  }
}

.country-flag img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.visit-content {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 16px;
}

.visit-copy {
  display: flex;
  min-width: 0;
  flex: 1 1 0;
  flex-direction: column;
  gap: 8px;
}

.visit-copy h3 {
  display: -webkit-box;
  width: 100%;
  min-height: 48px;
  margin: 0;
  overflow: hidden;
  color: var(--panel-text);
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  text-overflow: ellipsis;
  word-break: break-word;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.visit-meta {
  display: flex;
  width: 100%;
  align-items: flex-start;
  flex-direction: column;
  gap: 6px;
}

.visit-status-badge {
  display: inline-flex;
  min-height: 20px;
  flex-shrink: 0;
  align-items: center;
  border-radius: 2px;
  color: #fff;
  font-size: 11px;
  font-weight: 500;
  line-height: 16px;
  padding: 2px 8px;
}

.visit-meta p {
  width: 100%;
  margin: 0;
  overflow: hidden;
  color: var(--panel-text-subtle);
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.visit-carousel {
  position: relative;
  display: flex;
  width: 100%;
  min-width: 0;
  flex-shrink: 1;
  align-items: stretch;
}

.site-visit-page {
  display: grid;
  width: 100%;
  gap: 14px;
  min-height: 118px;
  overflow: hidden;
}

.site-visit-item {
  display: grid;
  width: 100%;
  min-width: 0;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 1.35fr);
  align-items: center;
  gap: 16px;
  padding-block: 14px;
  border-top: 1px solid rgba(5, 21, 36, 0.07);

  &:first-child {
    padding-block-start: 0;
    border-top: 0;
  }
}

.site-visit-item-link {
  color: inherit;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.86;
  }
}

.visit-gallery {
  display: grid;
  width: 100%;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  overflow: hidden;
}

.visit-gallery-cooperative {
  grid-template-columns: repeat(2, 174px) 312px;
  gap: 16px;
  width: 692px;
}

.visit-image-wrap {
  position: relative;
  width: 100%;
  height: 98px;
  aspect-ratio: auto;
  overflow: hidden;
  border-radius: 8px;
  background: #eef0f3;
}

.visit-gallery-cooperative .visit-image-wrap {
  width: auto;
}

.visit-gallery-cooperative .visit-image-wrap-map {
  width: 312px;
  height: 98px;
}

.visit-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.visit-map-pin {
  position: absolute;
  z-index: 1;
  left: 50%;
  top: 50%;
  width: 88px;
  height: 88px;
  pointer-events: none;
  transform: translate(-50%, -54%);
  animation: visit-map-pin-bounce 1.8s ease-in-out infinite;
}

.visit-map-pin-ring {
  position: absolute;
  z-index: 0;
  left: 50%;
  top: 36px;
  width: 40px;
  height: 40px;
  border: 1px solid rgba(42, 141, 107, 0.32);
  border-radius: 999px;
  background: rgba(42, 141, 107, 0.2);
  transform-origin: 50% 50%;
  transform: translateX(-50%);
  animation: visit-map-pin-ring-pulse 1.6s ease-in-out infinite;
}

.visit-map-pin-shadow {
  position: absolute;
  z-index: 1;
  left: 50%;
  bottom: 38%;
  width: 8px;
  height: 3px;
  border-radius: 100%;
  background: rgba(33, 35, 43, 0.92);
  transform: translateX(-50%);
  animation: visit-map-pin-shadow-pulse 1.8s ease-in-out infinite;
}

.visit-map-pin-body {
  position: absolute;
  z-index: 2;
  top: 27%;
  left: 38%;
  width: 21px;
  height: 28px;
  border: 0;
  border-radius: 0;
  background: transparent;
  filter: drop-shadow(0 2px 6px rgba(5, 21, 36, 0.2));
}

.visit-map-pin-body::before {
  position: absolute;
  inset: 0;
  background: #25a56f;
  clip-path: path('M10.5 28C7.4 23.1 0 16.9 0 10.5C0 4.7 4.7 0 10.5 0S21 4.7 21 10.5C21 16.9 13.6 23.1 10.5 28Z');
  content: '';
}

.visit-map-pin-body::after {
  position: absolute;
  top: 7px;
  left: 50%;
  width: 9px;
  height: 9px;
  border-radius: 999px;
  background: #ffffff;
  content: '';
  transform: translateX(-50%);
}

@keyframes visit-map-pin-bounce {
  0%,
  100% {
    transform: translate(-50%, -54%);
  }

  45% {
    transform: translate(-50%, -62%);
  }

  50% {
    transform: translate(-50%, -64%);
  }
}

@keyframes visit-map-pin-ring-pulse {
  0%,
  100% {
    opacity: 0.88;
    transform: translateX(-50%) scale(0.96);
  }

  50% {
    opacity: 1;
    transform: translateX(-50%) scale(1.02);
  }
}

@keyframes visit-map-pin-shadow-pulse {
  0%,
  100% {
    opacity: 0.92;
    transform: translateX(-50%) scale(0.92);
  }

  50% {
    opacity: 0.78;
    transform: translateX(-50%) scale(0.78);
  }
}

.visit-arrow {
  position: absolute;
  z-index: 2;
  top: 45%;
  display: inline-flex;
  width: 32px;
  height: 32px;
  padding: 0;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 999px;
  background: rgba(5, 12, 31, 0.46);
  color: #fff;
  cursor: pointer;
  font-size: 23px;
  line-height: 1;
  opacity: 0;
  transform: translateY(-50%);
  transition:
    opacity 0.2s ease,
    background-color 0.2s ease;

  &:hover {
    background: rgba(5, 12, 31, 0.62);
  }
}

.visit-carousel:hover .visit-arrow {
  opacity: 1;
}

.visit-arrow-glyph {
  display: inline-flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  line-height: 1;
  transform: translateY(-2px);
}

.visit-arrow-prev {
  left: 8px;
}

.visit-arrow-next {
  right: 8px;
}

.visit-dots {
  display: flex;
  justify-content: center;
  gap: 4px;
  height: 6px;
}

.visit-dot {
  width: 6px;
  height: 6px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: #d9d9d9;
  cursor: pointer;
}

.visit-dot-active {
  width: 14px;
  background: #292a2e;
}

.visit-summary {
  display: -webkit-box;
  margin: 0;
  overflow: hidden;
  color: var(--panel-text-subtle);
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  text-overflow: ellipsis;
  word-break: break-word;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.site-feedback-panel-site-visit-only {
  .visit-copy {
    gap: 10px;
  }

  .visit-copy h3 {
    min-height: auto;
  }

  .visit-meta {
    align-items: center;
    flex-direction: row;
    gap: 8px;
  }

  .visit-meta p {
    width: auto;
  }
}

.site-feedback-divider {
  width: 1px;
  height: 122px;
  margin-top: 24px;
  background: var(--panel-divider);
}

.feedback-region {
  position: relative;
  flex: 1;
  gap: 20px;
}

.feedback-heading {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 8px;
}

.feedback-heading-action {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 4px;
}

.feedback-count {
  color: var(--panel-text-subtle);
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
}

.more-btn {
  display: inline-flex;
  width: 60px;
  height: 60px;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--panel-text-subtle);
  cursor: pointer;
  font-size: 20px;
  line-height: 1;
  transition:
    background-color 0.2s ease,
    color 0.2s ease;

  &:hover {
    color: #292a2e;
  }

  &:focus-visible {
    color: #292a2e;
    box-shadow: 0 0 0 2px rgba(41, 42, 46, 0.18);
  }
}

.feedback-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
  overflow: hidden;
}

.feedback-region-full .feedback-list {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.feedback-item {
  min-width: 0;
  border-radius: 8px;

  &:hover {
    .feedback-content,
    h3 {
      color: #292a2e;
    }
  }
}

.feedback-author {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 12px;
}

.feedback-avatar {
  display: inline-flex;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  flex: 0 0 40px;
  overflow: hidden;
  border: 0.5px solid rgba(5, 21, 36, 0.06);
  border-radius: 999px;
  background: #f3f1eb;
  color: var(--panel-text);
  font-size: 16px;
  font-weight: 600;
  object-fit: cover;
}

.author-info {
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  flex-direction: column;
  justify-content: center;
}

.author-info h3 {
  overflow: hidden;
  margin: 0;
  color: var(--panel-text);
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.author-info p {
  margin: 0;
  color: var(--panel-text-subtlest);
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
}

.feedback-content {
  display: -webkit-box;
  margin: 16px 0 0;
  overflow: hidden;
  color: var(--panel-text);
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  text-overflow: ellipsis;
  word-break: break-word;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

.site-feedback-panel-cooperative {
  min-height: 244px;
  padding: 36px 0;

  .site-visit-region {
    width: 100%;
    max-width: none;
    padding-inline-end: 0;
  }

  .visit-content {
    align-items: center;
    flex-direction: row;
    gap: 16px;
  }

  .visit-copy {
    width: 100%;
    flex: 494 1 0;
  }

  .visit-copy h3 {
    min-height: 24px;
    -webkit-line-clamp: 1;
    white-space: nowrap;
  }

  .visit-carousel {
    width: 100%;
    flex: 364 1 0;
  }

  .visit-gallery,
  .visit-gallery-cooperative {
    grid-template-columns: repeat(2, 174px) 312px;
    width: 100%;
  }

  .visit-image-wrap {
    width: 100%;
    height: 98px;
    aspect-ratio: auto;
  }
}

.site-feedback-panel-cooperative.site-feedback-panel-site-visit-only {
  .site-visit-item {
    grid-template-columns: minmax(0, 1fr) 692px;
  }

  .visit-copy h3 {
    -webkit-line-clamp: 2;
    white-space: normal;
  }
}

@media screen and (max-width: 799px) {
  .site-feedback-panel {
    align-items: stretch;
    flex-direction: column;
    gap: 28px;
  }

  .site-visit-region {
    width: 100%;
    max-width: none;
  }

  .site-visit-item {
    grid-template-columns: 1fr;
    align-items: stretch;
    gap: 12px;
  }

  .visit-arrow {
    opacity: 1;
  }

  .site-feedback-divider {
    width: 100%;
    height: 1px;
    flex-basis: 1px;
  }

  .feedback-region-full .feedback-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .feedback-heading {
    order: 1;
    padding-inline-end: 0;
  }

  .feedback-list {
    order: 3;
  }

  .site-feedback-panel-cooperative {
    min-height: auto;

    .visit-content {
      align-items: stretch;
      flex-direction: column;
    }

    .visit-copy,
    .visit-carousel,
    .visit-gallery {
      width: 100%;
      flex-basis: auto;
    }

    .visit-gallery-cooperative {
      grid-template-columns: repeat(3, minmax(0, 1fr));
      width: 100%;
    }

    .visit-gallery-cooperative .visit-image-wrap-map,
    .visit-gallery-cooperative .visit-image-wrap {
      width: 100%;
    }
  }
}

@media screen and (max-width: 720px) {
  .site-feedback-panel {
    padding: 28px 0;
  }

  .visit-copy h3 {
    min-height: auto;
  }

  .visit-content {
    align-items: stretch;
    flex-direction: column;
  }

  .visit-gallery,
  .visit-gallery-cooperative {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;
  }

  .visit-image-wrap {
    width: 100%;
    height: auto;
    aspect-ratio: 174 / 98;
  }

  .feedback-list,
  .feedback-region-full .feedback-list {
    grid-template-columns: 1fr;
  }

  .site-feedback-panel-cooperative {
    min-height: auto;
    padding: 28px 0;

    .visit-content {
      align-items: stretch;
      flex-direction: column;
    }

    .visit-copy,
    .visit-carousel,
    .visit-gallery {
      width: 100%;
      flex-basis: auto;
    }

    .visit-gallery-cooperative {
      grid-template-columns: 1fr;
    }

    .visit-gallery-cooperative .visit-image-wrap-map,
    .visit-gallery-cooperative .visit-image-wrap {
      width: 100%;
      height: auto;
      aspect-ratio: 174 / 98;
    }

    .visit-copy h3 {
      white-space: normal;
    }
  }
}
</style>
